import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const SERIES = [
  { key: "weight", label: "Peso (kg)", color: "#0EA5E9" },
  { key: "muscle_mass", label: "Massa Muscular (%)", color: "#22C55E" },
  { key: "body_fat", label: "Gordura Corporal (%)", color: "#EF4444" },
];

const fmtDate = (iso) => {
  const p = (iso || "").slice(0, 10).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : "";
};

// Média móvel simples (janela w) — ignora valores ausentes (NaN)
function sma(vals, w = 3) {
  return vals.map((_, i) => {
    let acc = 0, c = 0;
    for (let j = Math.max(0, i - w + 1); j <= i; j++) {
      if (!isNaN(vals[j])) { acc += vals[j]; c += 1; }
    }
    return c ? acc / c : NaN;
  });
}

function pathFrom(pts) {
  const valid = pts.filter((p) => p.y !== null);
  return valid.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
}

function MetricChart({ testid, label, color, dates, values }) {
  const valid = values.filter((v) => !isNaN(v));
  const W = 560, H = 174, padL = 42, padR = 14, padT = 16, padB = 36;
  const n = values.length;
  const xFor = (i) => (n === 1 ? padL + (W - padL - padR) / 2 : padL + (i * (W - padL - padR)) / (n - 1));

  if (!valid.length) {
    return (
      <div data-testid={testid} className="bg-[#0F1520] border border-[#263147] rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1"><span className="w-3 h-3 rounded-full" style={{ background: color }} /><span className="text-sm text-slate-200 font-medium">{label}</span></div>
        <p className="text-xs text-slate-500 py-6 text-center">Sem dados registrados.</p>
      </div>
    );
  }

  let min = Math.min(...valid), max = Math.max(...valid);
  const padv = (max - min) * 0.15 || Math.abs(max) * 0.1 || 1;
  min -= padv; max += padv;
  const range = max - min || 1;
  const yFor = (v) => padT + (1 - (v - min) / range) * (H - padT - padB);

  const ma = sma(values, 3);
  const pts = values.map((v, i) => ({ x: xFor(i), y: isNaN(v) ? null : yFor(v) }));
  const maPts = ma.map((v, i) => ({ x: xFor(i), y: isNaN(v) ? null : yFor(v) }));
  const yticks = [min, min + range / 2, max];

  return (
    <div data-testid={testid} className="bg-[#0F1520] border border-[#263147] rounded-2xl p-4">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: color }} /><span className="text-sm text-slate-200 font-medium">{label}</span></div>
        <div className="flex items-center gap-3 text-[10px] text-slate-500">
          <span className="flex items-center gap-1"><span className="w-4 h-[2px] rounded" style={{ background: color }} />valor</span>
          <span className="flex items-center gap-1"><span className="w-4 border-t border-dashed" style={{ borderColor: color }} />tendência</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[440px]" style={{ height: 174 }}>
          {yticks.map((t, gi) => {
            const y = yFor(t);
            return (
              <g key={gi}>
                <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="#263147" strokeWidth="1" />
                <text x={padL - 6} y={y + 3} fontSize="9" fill="#64748B" textAnchor="end">{Math.round(t * 10) / 10}</text>
              </g>
            );
          })}
          {/* Linha de tendência (média móvel) */}
          <path d={pathFrom(maPts)} fill="none" stroke={color} strokeOpacity="0.55" strokeWidth="1.5" strokeDasharray="5 4" strokeLinejoin="round" />
          {/* Linha do valor medido */}
          <path d={pathFrom(pts)} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
          {pts.map((p, i) => (p.y === null ? null : <circle key={i} cx={p.x} cy={p.y} r="3.5" fill={color} />))}
          {dates.map((dt, i) => (
            <text key={i} x={xFor(i)} y={H - 14} fontSize="8" fill="#64748B" textAnchor="end" transform={`rotate(-35 ${xFor(i)} ${H - 14})`}>{dt}</text>
          ))}
        </svg>
      </div>
    </div>
  );
}

export default function EvolutionChart({ history }) {
  // history: array oldest->newest of { created_at, bioimpedance }
  const rows = history.map((h) => ({
    date: fmtDate(h.created_at),
    weight: parseFloat(h.bioimpedance?.weight),
    muscle_mass: parseFloat(h.bioimpedance?.muscle_mass),
    body_fat: parseFloat(h.bioimpedance?.body_fat),
  }));

  if (rows.length < 1) {
    return <p className="text-sm text-slate-500 text-center py-8">Sem dados de evolução ainda.</p>;
  }

  const dates = rows.map((r) => r.date);
  const n = rows.length;
  const delta = (key) => {
    if (n < 2) return null;
    const a = rows[n - 2][key], b = rows[n - 1][key];
    if (isNaN(a) || isNaN(b)) return null;
    return Math.round((b - a) * 10) / 10;
  };

  return (
    <div data-testid="evolution-chart" className="space-y-4">
      {SERIES.map((s) => (
        <MetricChart key={s.key} testid={`evolution-chart-${s.key}`} label={s.label} color={s.color}
          dates={dates} values={rows.map((r) => r[s.key])} />
      ))}

      <div data-testid="evolution-variation" className="bg-[#0F1520] border border-[#263147] rounded-2xl p-4">
        <p className="text-xs text-slate-400 mb-2">Variação em relação à avaliação anterior</p>
        <div className="flex flex-wrap gap-4">
          {SERIES.map((s) => {
            const d = delta(s.key);
            const latest = rows[n - 1][s.key];
            const Icon = d === null || d === 0 ? Minus : d > 0 ? TrendingUp : TrendingDown;
            return (
              <div key={s.key} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ background: s.color }} />
                <span className="text-xs text-slate-300">{s.label}</span>
                <span className="text-xs font-semibold text-slate-100">{isNaN(latest) ? "—" : latest}</span>
                {d !== null && (
                  <span className={`text-[11px] flex items-center gap-0.5 ${d > 0 ? "text-emerald-400" : d < 0 ? "text-red-400" : "text-slate-500"}`}>
                    <Icon className="w-3 h-3" />{d > 0 ? "+" : ""}{d}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
